// UserKpiDialog.jsx - Dialog for Assigning KPI to User

import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    CircularProgress,
    Divider,
    IconButton,
    Alert,
    Autocomplete,
    Chip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormHelperText
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { userEmailSelector } from 'auth/redux.selectors';
import * as actions from '../redux.actions';
import * as selectors from '../redux.selectors';
import apiRequest from '@libs/apiRequest';

const CATEGORY_OPTIONS = [
    { value: 'Main', label: 'Main' },
    { value: 'Next Step', label: 'Next Step' },
    { value: 'Other', label: 'Other' }
];

// Weight mapping based on complexity
const COMPLEXITY_WEIGHT_MAP = {
    'lowest': 1,
    'low': 2,
    'medium': 3,
    'high': 4,
    'highest': 5
};

// Weight options for dropdown
const WEIGHT_OPTIONS = [
    { value: '1', label: 'Lowest' },
    { value: '2', label: 'Low' },
    { value: '3', label: 'Medium' },
    { value: '4', label: 'High' },
    { value: '5', label: 'Highest' }
];

const UserKpiDialog = () => {
    const dispatch = useDispatch();
    const open = useSelector(selectors.selectUserKpiDialogOpen);
    const loggedInUserEmail = useSelector(userEmailSelector);
    
    // Get dropdown data from Redux - ensure they're always arrays
    const users = useSelector(selectors.selectAllUsers) || [];
    const periods = useSelector(selectors.selectAllPeriods) || [];
    const templates = useSelector(selectors.selectAllTemplates) || [];
    const availableGoals = useSelector(selectors.selectAllGoals) || [];
    const departments = useSelector(selectors.selectAllDepartments) || [];
    const loadingDropdowns = useSelector(selectors.selectLoadingDropdowns);

    const availableUsers = React.useMemo(() => {
        if (!Array.isArray(users)) return [];
        if (!loggedInUserEmail) return users;
        return users.filter(u => (u.user?.email || u.employeeId) !== loggedInUserEmail);
    }, [users, loggedInUserEmail]);
    
    const [loadingTemplate, setLoadingTemplate] = useState(false);
    
    const [selectedUser, setSelectedUser] = useState(null);
    const [selectedPeriod, setSelectedPeriod] = useState(null);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [goals, setGoals] = useState([]);
    const [errors, setErrors] = useState({});

    // Fetch dropdown data when dialog opens
    useEffect(() => {
        if (open) {
            dispatch(actions.fetchAllUsersRequest());
            dispatch(actions.fetchAllPeriodsRequest());
            dispatch(actions.fetchAllTemplatesRequest());
            dispatch(actions.fetchAllGoalsRequest());
            dispatch(actions.fetchAllDepartmentsRequest());
        }
    }, [open, dispatch]);

    const handleLoadTemplate = async () => {
        if (!selectedTemplate) return;
        
        setLoadingTemplate(true);
        try {
            // If template already has goals loaded (from /kpi/template/get/all), use those
            if (selectedTemplate.goals && Array.isArray(selectedTemplate.goals)) {
                const templateGoals = selectedTemplate.goals
                    .filter(tg => !goals.some(g => g.goalId === tg.goalId))
                    .map(tg => ({
                        goalId: tg.goalId,
                        goalName: tg.goal?.name || '',
                        category: tg.category || 'Main',
                        weight: parseFloat(tg.weight) || 0,
                        score: null,
                        remark: tg.remark || ''
                    }));
                setGoals([...goals, ...templateGoals]);
                setLoadingTemplate(false);
                return;
            }

            // Otherwise fetch template with goals
            const response = await apiRequest({
                url: `/kpi/template/get/${selectedTemplate.id}/with-goals`,
                method: 'GET'
            });
            
            if (response && Array.isArray(response.goals)) {
                const templateGoals = response.goals
                    .filter(tg => !goals.some(g => g.goalId === tg.goalId))
                    .map(tg => ({
                        goalId: tg.goalId,
                        goalName: tg.goal?.name || '',
                        category: tg.category || 'Main',
                        weight: parseFloat(tg.weight) || 0,
                        score: null,
                        remark: tg.remark || ''
                    }));
                setGoals([...goals, ...templateGoals]);
            }
        } catch (error) {
            console.error('Failed to load template:', error);
        } finally {
            setLoadingTemplate(false);
        }
    };

    const handleAddGoal = () => {
        setGoals([{
            goalId: '',
            goalName: '',
            category: 'Main',  // Default category
            weight: '',  // Empty string instead of 0 so user must enter value
            score: null,
            remark: ''
        }, ...goals]);
    };

    const handleRemoveGoal = (index) => {
        setGoals(goals.filter((_, i) => i !== index));
    };

    const handleGoalChange = (index, field, value) => {
        const newGoals = [...goals];
        newGoals[index][field] = value;
        
        if (field === 'goalId') {
            const goal = availableGoals.find(g => g.id === value);
            if (goal) {
                newGoals[index].goalName = goal.name;
                // Auto-fill weight based on complexity when goal is selected
                const defaultWeight = COMPLEXITY_WEIGHT_MAP[goal.complexity] || 3;
                newGoals[index].weight = defaultWeight;
            }
        }
        
        setGoals(newGoals);
    };

    const validateForm = () => {
        const newErrors = {};

        if (!selectedUser) {
            newErrors.user = 'User is required';
        }
        if (!selectedPeriod) {
            newErrors.period = 'Period is required';
        }
        if (goals.length === 0) {
            newErrors.goals = 'At least one goal is required';
        }

        // NO 100% weight requirement - removed as per client request

        goals.forEach((goal, index) => {
            if (!goal.goalId) {
                newErrors[`goal-${index}`] = 'Goal is required';
            }
            if (!goal.category) {
                newErrors[`category-${index}`] = 'Category is required';
            }
            if (!goal.weight || goal.weight === '') {
                newErrors[`weight-${index}`] = 'Weight is required';
            } else {
                const weight = Number(goal.weight);
                if (isNaN(weight) || weight < 1 || weight > 5) {
                    newErrors[`weight-${index}`] = 'Please select a valid weight';
                }
            }
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = () => {
        if (!validateForm()) return;

        const data = {
            userId: selectedUser.user?.email || selectedUser.employeeId,
            periodId: selectedPeriod.id,
            manualGoals: goals.map(g => ({
                goalId: g.goalId,
                category: g.category,
                weight: Number(g.weight)
            }))
        };

        dispatch(actions.assignUserKpiRequest(data));
        handleClose();
    };

    const handleClose = () => {
        dispatch(actions.closeUserKpiDialog());
        // Reset form
        setSelectedUser(null);
        setSelectedPeriod(null);
        setSelectedTemplate(null);
        setGoals([]);
        setErrors({});
    };

    const totalWeight = goals.reduce((sum, g) => sum + (Number(g.weight) || 0), 0);
    
    // Simple validation - NO 100% weight requirement
    const isFormValid = () => {
        // Must have user, period
        if (!selectedUser || !selectedPeriod) return false;
        
        // Must have at least one goal
        if (goals.length === 0) return false;
        
        // All goals must have goalId, category, and weight > 0
        const allGoalsComplete = goals.every(g => 
            g.goalId && g.category && Number(g.weight) > 0
        );
        
        return allGoalsComplete;
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">Assign KPI to User</Typography>
                    <IconButton onClick={handleClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            
            <DialogContent dividers sx={{ maxHeight: '60vh', overflowY: 'auto' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {/* User Selection */}
                    <Autocomplete
                        options={availableUsers}
                        getOptionLabel={(option) => {
                            const name = [option.firstname, option.middlename, option.lastname].filter(Boolean).join(' ').trim();
                            const email = option.user?.email || option.employeeId || '';
                            return name ? `${name} (${email})` : email;
                        }}
                        value={selectedUser}
                        onChange={(e, newValue) => setSelectedUser(newValue)}
                        loading={loadingDropdowns}
                        size="small"
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="User *"
                                error={!!errors.user}
                                helperText={errors.user}
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {loadingDropdowns ? <CircularProgress color="inherit" size={20} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                        renderOption={(props, option) => (
                            <li {...props}>
                                <Box>
                                    <Typography variant="body2">
                                        {[option.firstname, option.middlename, option.lastname].filter(Boolean).join(' ').trim() || option.user?.email || option.employeeId}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {option.user?.email || option.employeeId} • {option.designation?.name || '--'}
                                    </Typography>
                                </Box>
                            </li>
                        )}
                    />

                    {/* Selected User Role Display */}
                    {selectedUser && (
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: -2 }}>
                            {selectedUser.designation?.name && (
                                <Typography variant="body2" color="text.secondary">
                                    <strong>Role:</strong> {selectedUser.designation.name}
                                </Typography>
                            )}
                            {selectedUser.department?.name && (
                                <Typography variant="body2" color="text.secondary">
                                    <strong>Department:</strong> {selectedUser.department.name}
                                </Typography>
                            )}
                        </Box>
                    )}

                    {/* Period Selection */}
                    <Autocomplete
                        options={periods}
                        getOptionLabel={(option) => option.name || ''}
                        value={selectedPeriod}
                        onChange={(e, newValue) => setSelectedPeriod(newValue)}
                        loading={loadingDropdowns}
                        size="small"
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Period *"
                                error={!!errors.period}
                                helperText={errors.period}
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {loadingDropdowns ? <CircularProgress color="inherit" size={20} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                        renderOption={(props, option) => (
                            <li {...props}>
                                <Box>
                                    <Typography variant="body2">{option.name}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {option.startDate} - {option.endDate}
                                    </Typography>
                                </Box>
                            </li>
                        )}
                    />

                    <Divider />

                    {/* Template Selection (Optional) */}
                    <Box>
                        <Typography variant="subtitle2" gutterBottom>
                            Copy from Template (Optional)
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Autocomplete
                                sx={{ flexGrow: 1 }}
                                options={templates}
                                getOptionLabel={(option) => option.name || ''}
                                value={selectedTemplate}
                                onChange={(e, newValue) => setSelectedTemplate(newValue)}
                                loading={loadingDropdowns}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Select Template"
                                        size="small"
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {loadingDropdowns ? <CircularProgress color="inherit" size={20} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                            />
                            <Button
                                variant="outlined"
                                startIcon={<ContentCopyIcon />}
                                onClick={handleLoadTemplate}
                                disabled={!selectedTemplate || loadingTemplate}
                            >
                                {loadingTemplate ? <CircularProgress size={20} /> : 'Copy'}
                            </Button>
                        </Box>
                    </Box>

                    <Divider />

                    {/* Goals Management */}
                    <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Box>
                                <Typography variant="subtitle2">
                                    Goals * {goals.length > 0 && `(${goals.length})`}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Total Weight: {totalWeight}
                                </Typography>
                            </Box>
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<AddIcon />}
                                onClick={handleAddGoal}
                            >
                                Add Goal
                            </Button>
                        </Box>

                        {errors.goals && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {errors.goals}
                            </Alert>
                        )}

                        {goals.map((goal, index) => (
                            <Box key={index} sx={(theme) => ({ mb: 2, p: 2, border: `1px solid ${theme.palette.divider}`, borderRadius: 1 })}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Typography variant="body2" fontWeight={600}>
                                        Goal #{index + 1}
                                    </Typography>
                                    <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => handleRemoveGoal(index)}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Box>

                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <Autocomplete
                                        options={availableGoals.filter(goal => 
                                            !goals.some((g, gIndex) => gIndex !== index && g.goalId === goal.id)
                                        )}
                                        getOptionLabel={(option) => option.name || ''}
                                        value={availableGoals.find(g => g.id === goal.goalId) || null}
                                        onChange={(e, newValue) => {
                                            handleGoalChange(index, 'goalId', newValue ? newValue.id : '');
                                        }}
                                        size="small"
                                        filterOptions={(options, { inputValue }) => {
                                            if (!inputValue) return options;

                                            const searchLower = inputValue.toLowerCase();

                                            return options.filter(option => {
                                                // Search in goal name
                                                const nameMatch = option.name?.toLowerCase().includes(searchLower);

                                                // Search in complexity
                                                const complexityMatch = option.complexity?.toLowerCase().includes(searchLower);

                                                // Search in department names
                                                let departmentMatch = false;
                                                if (option.departmentIds && option.departmentIds.length > 0) {
                                                    departmentMatch = option.departmentIds.some(deptId => {
                                                        const dept = departments.find(d => String(d.id) === String(deptId));
                                                        return dept && dept.name?.toLowerCase().includes(searchLower);
                                                    });
                                                }

                                                // Return true if any field matches
                                                return nameMatch || complexityMatch || departmentMatch;
                                            });
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Goal *"
                                                size="small"
                                                placeholder="Select a goal"
                                                error={!!errors[`goal-${index}`]}
                                                helperText={errors[`goal-${index}`]}
                                            />
                                        )}
                                        renderOption={(props, option) => {
                                            // Map departmentIds to department names
                                            let departmentNames = 'All Departments';
                                            if (option.departmentIds && option.departmentIds.length > 0) {
                                                const deptNames = option.departmentIds
                                                    .map(deptId => {
                                                        const dept = departments.find(d => String(d.id) === String(deptId));
                                                        return dept ? dept.name : null;
                                                    })
                                                    .filter(name => name !== null);

                                                departmentNames = deptNames.length > 0 ? deptNames.join(', ') : 'All Departments';
                                            }

                                            return (
                                                <Box component="li" {...props}>
                                                    <Box sx={{ width: '100%' }}>
                                                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                                            {option.name}
                                                        </Typography>
                                                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5, flexWrap: 'wrap' }}>
                                                            <Chip
                                                                label={option.complexity}
                                                                size="small"
                                                                color="primary"
                                                                variant="outlined"
                                                                sx={{
                                                                    height: 20,
                                                                    fontSize: '0.7rem',
                                                                    textTransform: 'capitalize'
                                                                }}
                                                            />
                                                            <Chip
                                                                label={departmentNames}
                                                                size="small"
                                                                color="secondary"
                                                                variant="outlined"
                                                                sx={{
                                                                    height: 20,
                                                                    fontSize: '0.7rem',
                                                                    maxWidth: 200
                                                                }}
                                                            />
                                                        </Box>
                                                        <Typography variant="caption" color="text.secondary" sx={{
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: 1,
                                                            WebkitBoxOrient: 'vertical',
                                                            overflow: 'hidden',
                                                            mt: 0.5
                                                        }}>
                                                            {option.description || 'No description'}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            );
                                        }}
                                    />

                                    {/* Show selected goal details */}
                                    {availableGoals.find(g => g.id === goal.goalId) && (
                                        <Box sx={{ p: 1.5, bgcolor: 'background.default', borderRadius: 1 }}>
                                            {(() => {
                                                const selectedGoal = availableGoals.find(g => g.id === goal.goalId);
                                                const getDepartmentName = (deptId) => {
                                                    if (!deptId || deptId === null || deptId === undefined) return 'All Departments';
                                                    const dept = departments.find(d => d.id === deptId);
                                                    return dept?.name || 'All Departments';
                                                };
                                                const departmentName = getDepartmentName(selectedGoal.departmentId);
                                                return (
                                                    <>
                                                        <Typography variant="caption" color="text.secondary" gutterBottom>
                                                            Selected Goal Details:
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.5 }}>
                                                            {selectedGoal.name}
                                                        </Typography>
                                                        {selectedGoal.description && (
                                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                                                {selectedGoal.description}
                                                            </Typography>
                                                        )}
                                                        <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                                                            <Typography variant="caption">
                                                                <strong>Complexity:</strong> {selectedGoal.complexity}
                                                            </Typography>
                                                            <Typography variant="caption">
                                                                <strong>Department:</strong> {departmentName}
                                                            </Typography>
                                                        </Box>
                                                    </>
                                                );
                                            })()}
                                        </Box>
                                    )}

                                    {/* Category Selection */}
                                    <Autocomplete
                                        fullWidth
                                        options={CATEGORY_OPTIONS}
                                        getOptionLabel={(option) => option.label}
                                        value={CATEGORY_OPTIONS.find(opt => opt.value === goal.category) || null}
                                        onChange={(event, newValue) => {
                                            handleGoalChange(index, 'category', newValue ? newValue.value : 'Main');
                                        }}
                                        size="small"
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Category *"
                                                placeholder="Select category"
                                                error={!!errors[`category-${index}`]}
                                                helperText={errors[`category-${index}`]}
                                            />
                                        )}
                                    />

                                    {/* Weight Selection */}
                                    <FormControl fullWidth size="small" error={!!errors[`weight-${index}`]}>
                                        <InputLabel>Weight *</InputLabel>
                                        <Select
                                            value={goal.weight}
                                            onChange={(e) => handleGoalChange(index, 'weight', e.target.value)}
                                            label="Weight *"
                                        >
                                            {WEIGHT_OPTIONS.map((option) => (
                                                <MenuItem key={option.value} value={option.value}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                                                        <Typography variant="body2" sx={{ fontSize: '16px' }}>
                                                            {option.label}
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.secondary', ml: 1 }}>
                                                            score: {option.value}
                                                        </Typography>
                                                    </Box>
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        <FormHelperText>
                                            {errors[`weight-${index}`] || 'Weight based on goal complexity (1-5). Auto-filled when goal selected.'}
                                        </FormHelperText>
                                    </FormControl>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
                <Button onClick={handleClose} variant="outlined" size="small">
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={!isFormValid()}
                    size="small"
                >
                    Assign KPI
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default UserKpiDialog;
